/**
 * Which service an organisation's mail leaves through, and what it needs.
 *
 * Named in the domain rather than in the worker's configuration, because it is
 * now a customer-facing choice: an organisation picks a provider and supplies
 * its own credentials, and the screen, the contract and the vault all have to
 * agree about the fields. One table, so a seventh provider is an entry rather
 * than a change in five places.
 *
 * `dummy` is deliberately not here. It exists for exercising failure paths in
 * CI and offering it to a customer would be offering a provider that silently
 * delivers nothing.
 */
/**
 * What each provider asks for, in the order a person fills it in.
 *
 * Every value is stored sealed, secret or not: the split is about what a screen
 * may show back, not about what is worth encrypting. A region is not a secret,
 * but keeping one row's worth of settings in one sealed blob means there is one
 * thing to open and one thing to rotate.
 */
export const PROVIDER_FIELDS = {
    ses: [
        {
            key: 'SES_REGION',
            label: 'Region',
            secret: false,
            required: true,
            hint: 'The AWS region your identity is verified in, such as eu-west-1.',
        },
        { key: 'SES_ACCESS_KEY_ID', label: 'Access key id', secret: false, required: true, hint: '' },
        {
            key: 'SES_SECRET_ACCESS_KEY',
            label: 'Secret access key',
            secret: true,
            required: true,
            hint: '',
        },
        {
            key: 'SES_CONFIGURATION_SET',
            label: 'Configuration set',
            secret: false,
            required: false,
            hint: 'Optional. Routes bounce and complaint notifications back to us.',
        },
    ],
    postmark: [
        { key: 'POSTMARK_SERVER_TOKEN', label: 'Server token', secret: true, required: true, hint: '' },
        {
            key: 'POSTMARK_MESSAGE_STREAM',
            label: 'Message stream',
            secret: false,
            required: false,
            hint: 'Optional, and worth setting: campaigns sharing a stream with transactional mail damages the reputation receipts depend on.',
        },
    ],
    resend: [{ key: 'RESEND_API_KEY', label: 'API key', secret: true, required: true, hint: '' }],
    brevo: [{ key: 'BREVO_API_KEY', label: 'API key', secret: true, required: true, hint: '' }],
    smtp: [
        { key: 'SMTP_HOST', label: 'Host', secret: false, required: true, hint: '' },
        {
            key: 'SMTP_PORT',
            label: 'Port',
            secret: false,
            required: true,
            hint: '587 for STARTTLS, 465 for implicit TLS.',
        },
        { key: 'SMTP_USER', label: 'Username', secret: false, required: true, hint: '' },
        { key: 'SMTP_PASSWORD', label: 'Password', secret: true, required: true, hint: '' },
    ],
    /*
     * Nothing typed in. Both values arrive from Google's consent screen and are
     * sealed like every other provider's settings — they are listed so that
     * `missingFields` can still say a half-finished connection is not usable.
     *
     * A refresh token, not a password: Gmail's SMTP takes an app password, which
     * means asking somebody to create one, which is the friction this replaces.
     */
    gmail: [
        {
            key: 'GMAIL_REFRESH_TOKEN',
            label: 'Google account',
            secret: true,
            required: true,
            hint: '',
        },
        /*
         * **Not secret, and it matters that it is not.**
         *
         * It is the address recipients see in the From line — the least secret
         * thing in a campaign. Marked secret it could not be read back, which left
         * the settings screen able to say "gmail is configured" and unable to say
         * *which account*, and left nothing able to notice that the campaign's From
         * address is a different one. Gmail refuses a message whose From is neither
         * the authenticated account nor a verified alias of it, so that mismatch is
         * a campaign where every single message comes back rejected.
         */
        {
            key: 'GMAIL_ADDRESS',
            label: 'Sends as',
            secret: false,
            required: true,
            hint: 'The Google account that was connected. Gmail will only send as this address, or as an alias verified inside it.',
        },
    ],
};
/**
 * The one address a provider is locked to, when it is locked to one.
 *
 * Gmail is the only provider here that is. Every other one sends as whatever
 * the campaign says, and enforces From addresses through domain verification
 * on their side rather than through a single account — so answering anything
 * for them would be inventing a restriction this product imposed on somebody
 * else's account.
 */
export function sendingIdentityOf(provider, settings) {
    if (provider !== 'gmail')
        return undefined;
    const address = (settings.GMAIL_ADDRESS ?? '').trim();
    return address === '' ? undefined : address;
}
export const EMAIL_PROVIDERS = Object.keys(PROVIDER_FIELDS);
export function isEmailProvider(value) {
    return Object.hasOwn(PROVIDER_FIELDS, value);
}
/**
 * Which required fields a set of settings is missing.
 *
 * Returned rather than thrown, and returned as a list rather than the first
 * one: somebody filling in four SMTP fields should learn about all the blanks
 * at once instead of one per attempt. The same reason `readConfig` reports every
 * missing variable together. (I9)
 */
export function missingFields(provider, settings) {
    return PROVIDER_FIELDS[provider]
        .filter((field) => field.required && (settings[field.key] ?? '').trim() === '')
        .map((field) => field.label);
}
/**
 * Providers connected by signing in rather than by typing a key.
 *
 * Gmail is the first. The difference is not cosmetic: there is nothing for
 * somebody to paste, the credential arrives from Google and is sealed without
 * ever being shown, and a screen that drew text boxes for it would be asking
 * for something nobody has. The settings screen asks this before deciding
 * whether to draw a form or a button.
 */
const CONNECTED_BY_SIGNING_IN = ['gmail'];
export function isSignedInProvider(provider) {
    return CONNECTED_BY_SIGNING_IN.includes(provider);
}
/**
 * How many messages a provider will accept from one account in a day.
 *
 * **Only where the provider imposes one that a customer will actually hit.**
 * Gmail is 500 recipients a day on a free account and 2,000 on Workspace, and
 * the difference is not something this product can see from a token — so the
 * lower number is the one enforced. Somebody on Workspace who wants their
 * 2,000 is somebody who should be on SES, and the screen says so.
 *
 * SES, Postmark, Resend and Brevo all have quotas too; none is listed here
 * because they are account-specific, negotiable and usually far larger than a
 * list this product holds. A cap invented for them would be a limit this
 * product imposed on somebody else's account.
 */
/** Not exported: `dailyCapFor` is how anything asks, so there is one way to ask. */
const DAILY_CAP = {
    gmail: 500,
};
/** What a provider will take today, or nothing when it imposes no limit here. */
export function dailyCapFor(provider) {
    return DAILY_CAP[provider];
}
//# sourceMappingURL=emailProvider.js.map